import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';

import { FeatureFlag } from '~/lib/flags/flags';
import { getRuntimeStore } from '~/lib/flags/runtimeStore';

interface FeatureFlagParams {
  default?: boolean;
}

export type FeatureFlags = { [flag in FeatureFlag]: boolean };

/**
 * Hook to subscribe to runtime flag changes and trigger re-renders
 * Only active during Cypress testing
 */
const useRuntimeFlagStore = () => {
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const runtimeStore = getRuntimeStore();
    if (!runtimeStore) {
      return; // No runtime store in production
    }

    const unsubscribe = runtimeStore.subscribe(() => {
      forceUpdate({});
    });
    return unsubscribe;
  }, []);

  return getRuntimeStore();
};

/**
 * Gets environment variable override for a feature flag
 * Environment variables should follow the pattern: VITE_FEATURE_FLAG_<FLAG_NAME_IN_CAPS>
 * Examples:
 * - enable-panini-check-scanner → VITE_FEATURE_FLAG_ENABLE_PANINI_CHECK_SCANNER
 * - dashboard-custom-roles → VITE_FEATURE_FLAG_DASHBOARD_CUSTOM_ROLES
 */
const getEnvOverride = (flag: FeatureFlag): boolean | undefined => {
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
    return undefined;
  }

  const envKey = `VITE_FEATURE_FLAG_${flag.toUpperCase().replace(/-/g, '_')}`;
  const envValue = import.meta.env[envKey];

  if (envValue === undefined) {
    return undefined;
  }

  if (typeof envValue === 'string') {
    const lowerValue = envValue.toLowerCase();
    if (lowerValue === 'true' || lowerValue === '1') {
      return true;
    }
    if (lowerValue === 'false' || lowerValue === '0') {
      return false;
    }
  }

  if (typeof envValue === 'boolean') {
    return envValue;
  }

  return undefined;
};

/**
 * Resolves a feature flag value with runtime and environment variable override support
 * Priority: Runtime Override → Environment Variable → LaunchDarkly Flag → Default Value
 */
const resolveFlagValue = (flag: FeatureFlag, launchDarklyValue: unknown, defaultValue?: boolean): boolean => {
  const runtimeStore = getRuntimeStore();
  if (runtimeStore) {
    const runtimeOverride = runtimeStore.getFlag(flag);
    if (runtimeOverride !== undefined) {
      return runtimeOverride;
    }
  }

  const envOverride = getEnvOverride(flag);
  if (envOverride !== undefined) {
    return envOverride;
  }

  if (typeof launchDarklyValue === 'boolean') {
    return launchDarklyValue;
  }

  return defaultValue ?? false;
};

export const useFeatureFlags = (): FeatureFlags => {
  const flagSet = useFlags();
  const store = useRuntimeFlagStore();

  return useMemo(() => {
    const allFlags = Object.values(FeatureFlag) as FeatureFlag[];

    return allFlags.reduce((acc, flag) => {
      const launchDarklyValue = flagSet[flag];
      acc[flag] = resolveFlagValue(flag, launchDarklyValue);
      return acc;
    }, {} as FeatureFlags);
  }, [flagSet, store]);
};

export const useFeatureFlag = (flag: FeatureFlag, params?: FeatureFlagParams): boolean => {
  const flagSet = useFlags();
  const store = useRuntimeFlagStore();

  return useMemo(() => {
    const launchDarklyValue = flagSet[flag];
    return resolveFlagValue(flag, launchDarklyValue, params?.default);
  }, [flag, flagSet, params?.default, store]);
};
